(function(){
  "use strict";

  // ---------- Hydrate compact payload ----------
  // Data ships dictionary-encoded (see rd-gemstones-data.js). Prices are not
  // stored per stone: unitPrice comes from the price table keyed by category,
  // and total is weight x unitPrice. To change pricing, edit RD_RAW.px only.
  function catOf(type, hue){
    if(type === "Sapphire"){
      const h = (hue || "").toLowerCase();
      if(h.indexOf("padparadscha") !== -1) return "Padparadscha";
      if(h.indexOf("pink") !== -1) return "Pink Sapphire";
      return "Blue Sapphire";
    }
    return type;
  }

  function hydrate(raw){
    return raw.d.map(function(r, i){
      const type  = raw.t[r[1]];
      const hue   = raw.h[r[7]];
      const code  = r[12] || "";
      const kind  = r[11];

      let imageUrl, videoUrl;
      if(kind === 0){
        imageUrl = "https://d360.tech/imaged/" + code + "/still.jpg";
        videoUrl = "https://d360.tech/view.html?d=" + code;
      } else if(kind === 1){
        imageUrl = "https://media.d360.us/imaged/" + code + "/still.jpg";
        videoUrl = "https://d360.tech/aview.html?d=" + code;
      } else {
        imageUrl = r[13] || "";
        videoUrl = code ? ("https://d360.tech/view.html?d=" + code) : "";
      }

      const weight = r[2];
      const unit   = raw.px[catOf(type, hue)] || 0;

      return {
        id: i + 1,
        stock: r[0],
        type: type,
        weight: weight,
        shape: raw.s[r[3]],
        quality: raw.q[r[4]],
        lab: raw.l[r[5]],
        colorTone: raw.o[r[6]],
        colorHue: hue,
        measurements: r[8],
        remarks: raw.r[r[9]],
        certNum: r[10],
        location: raw.loc,
        heated: "",
        imageUrl: imageUrl,
        videoUrl: videoUrl,
        unitPrice: unit,
        total: Math.round(weight * unit * 100) / 100,
      };
    });
  }

  const STONES = hydrate(RD_RAW);

  // ---------- State ----------
  let state = {
    type: "All",
    search: "",
    shape: "", hue: "", lab: "", quality: "",
    caratMin: null, caratMax: null, priceMin: null, priceMax: null,
    sortKey: "stock", sortDir: 1,
    view: "list",
    expanded: null,
  };
  let cart = []; // array of stone ids

  const columns = [
    { key: "shape", label: "Shape", sortable: true },
    { key: "type", label: "Stone Type", sortable: true },
    { key: "colorHue", label: "Color", sortable: true },
    { key: "quality", label: "Quality", sortable: true },
    { key: "weight", label: "Carat", sortable: true, numeric: true },
    { key: "lab", label: "Lab", sortable: true },
    { key: "location", label: "Location", sortable: true },
    { key: "unitPrice", label: "Price / ct", sortable: true, numeric: true },
    { key: "total", label: "Total", sortable: true, numeric: true },
    { key: "cart", label: "", sortable: false },
  ];

  // ---------- Helpers ----------
  function fmtMoney(n){
    return "$" + Number(n).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2});
  }
  function uniqueSorted(arr){
    return [...new Set(arr.filter(v => v !== "" && v != null))].sort();
  }
  function labClass(lab){
    if(!lab) return "";
    const l = lab.toLowerCase();
    if(l === "agl") return "lab-agl";
    if(l === "igitl") return "lab-igitl";
    return "lab-non";
  }

  // ---------- Categories ----------
  // Sapphire is split by hue so Padparadscha and Pink each get their own tab.
  function category(s){
    if(s.type === "Sapphire"){
      const hue = (s.colorHue || "").toLowerCase();
      if(hue.includes("padparadscha")) return "Padparadscha";
      if(hue.includes("pink")) return "Pink Sapphire";
      return "Blue Sapphire";
    }
    return s.type;
  }

  // Preferred tab order; anything unlisted is appended alphabetically.
  const CATEGORY_ORDER = [
    "Alexandrite",
    "Emerald",
    "Colombian Emerald",
    "Paraiba",
    "Ruby",
    "Blue Sapphire",
    "Padparadscha",
    "Pink Sapphire",
  ];

  function orderedCategories(){
    const present = new Set(STONES.map(category));
    const ordered = CATEGORY_ORDER.filter(c => present.has(c));
    const extras = [...present].filter(c => !CATEGORY_ORDER.includes(c)).sort();
    return ordered.concat(extras);
  }

  // ---------- Image handling ----------
  // Colour per gem category, used for the fallback placeholder
  const GEM_COLORS = {
    "Alexandrite": "#7a5aa8",
    "Emerald": "#1f7a52",
    "Colombian Emerald": "#186b46",
    "Paraiba": "#0f8fa8",
    "Ruby": "#a01f3c",
    "Blue Sapphire": "#1f4ea0",
    "Padparadscha": "#e0764a",
    "Pink Sapphire": "#c95a86",
    "Sapphire": "#1f4ea0",
  };

  // Inline SVG placeholder shown when a remote photo can't load,
  // so a blocked/missing image still reads as a gem rather than blank space.
  function placeholderDataUri(stone){
    const color = GEM_COLORS[category(stone)] || GEM_COLORS[stone.type] || "#5d6a61";
    const label = (stone.shape || "").slice(0, 10);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <rect width="100" height="100" fill="#eef2ec"/>
      <polygon points="50,20 72,42 50,80 28,42" fill="${color}" opacity="0.85"/>
      <polygon points="50,20 72,42 50,46 28,42" fill="#ffffff" opacity="0.28"/>
      <text x="50" y="94" font-family="Helvetica,Arial,sans-serif" font-size="9"
            fill="#5d6a61" text-anchor="middle">${label}</text>
    </svg>`;
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  }

  // Builds an <img> that: sends no referrer (avoids hotlink blocking),
  // lazy-loads, and swaps to the placeholder if the source fails.
  function stoneImg(stone, cls, extraAttrs){
    const fallback = placeholderDataUri(stone);
    const src = stone.imageUrl || fallback;
    return `<img class="${cls}" src="${src}" alt="${stone.type} ${stone.shape}"
      loading="lazy" referrerpolicy="no-referrer" ${extraAttrs || ""}
      onerror="this.onerror=null;this.src='${fallback}';this.classList.add('img-fallback');">`;
  }

  // ---------- Populate filter dropdowns ----------
  function populateFilters(){
    const shapeSel = document.getElementById("f-shape");
    const hueSel = document.getElementById("f-hue");
    const labSel = document.getElementById("f-lab");
    const qualSel = document.getElementById("f-quality");

    uniqueSorted(STONES.map(s=>s.shape)).forEach(v=>{
      shapeSel.insertAdjacentHTML("beforeend", `<option value="${v}">${v}</option>`);
    });
    uniqueSorted(STONES.map(s=>s.colorHue)).forEach(v=>{
      hueSel.insertAdjacentHTML("beforeend", `<option value="${v}">${v}</option>`);
    });
    uniqueSorted(STONES.map(s=>s.lab)).forEach(v=>{
      labSel.insertAdjacentHTML("beforeend", `<option value="${v}">${v}</option>`);
    });
    uniqueSorted(STONES.map(s=>s.quality)).forEach(v=>{
      qualSel.insertAdjacentHTML("beforeend", `<option value="${v}">${v}</option>`);
    });
  }

  function populateTypeTabs(){
    const cats = orderedCategories();
    const tabsEl = document.getElementById("typeTabs");
    const counts = {};
    STONES.forEach(s=>{ const c = category(s); counts[c] = (counts[c]||0)+1; });

    const all = [["All", "All", STONES.length]]
      .concat(cats.map(c => [c, c, counts[c]]));

    tabsEl.innerHTML = all.map(([val,label,n])=>
      `<div class="tab ${val==="All"?"active":""}" data-type="${val}">${label}<span class="tab-count">${n}</span></div>`
    ).join("");

    tabsEl.querySelectorAll(".tab").forEach(tab=>{
      tab.addEventListener("click", ()=>{
        state.type = tab.dataset.type;
        state.expanded = null;
        tabsEl.querySelectorAll(".tab").forEach(t=>t.classList.remove("active"));
        tab.classList.add("active");
        render();
      });
    });
  }

  // ---------- Filtering / sorting ----------
  function getFiltered(){
    let rows = STONES.filter(s=>{
      if(state.type !== "All" && category(s) !== state.type) return false;
      if(state.shape && s.shape !== state.shape) return false;
      if(state.hue && s.colorHue !== state.hue) return false;
      if(state.lab && s.lab !== state.lab) return false;
      if(state.quality && s.quality !== state.quality) return false;
      if(state.caratMin != null && s.weight < state.caratMin) return false;
      if(state.caratMax != null && s.weight > state.caratMax) return false;
      if(state.priceMin != null && s.total < state.priceMin) return false;
      if(state.priceMax != null && s.total > state.priceMax) return false;
      if(state.search){
        const q = state.search.toLowerCase();
        const hay = [s.stock, s.shape, s.type, s.colorHue, s.colorTone, s.certNum, s.quality].join(" ").toLowerCase();
        if(!hay.includes(q)) return false;
      }
      return true;
    });
    rows.sort((a,b)=>{
      let av = a[state.sortKey], bv = b[state.sortKey];
      if(typeof av === "string") av = av.toLowerCase();
      if(typeof bv === "string") bv = bv.toLowerCase();
      if(av < bv) return -1*state.sortDir;
      if(av > bv) return 1*state.sortDir;
      return 0;
    });
    return rows;
  }

  // ---------- Rendering: table ----------
  function renderHead(){
    const head = document.getElementById("tableHead");
    head.innerHTML = columns.map(c=>{
      if(!c.sortable) return `<th></th>`;
      const sorted = state.sortKey === c.key;
      const arrow = sorted ? (state.sortDir === 1 ? "&uarr;" : "&darr;") : "&udarr;";
      return `<th data-key="${c.key}" class="${sorted ? "sorted": ""}">${c.label} <span class="arrow">${arrow}</span></th>`;
    }).join("");
    head.querySelectorAll("th[data-key]").forEach(th=>{
      th.addEventListener("click", ()=>{
        const key = th.dataset.key;
        if(state.sortKey === key){ state.sortDir *= -1; }
        else { state.sortKey = key; state.sortDir = 1; }
        render();
      });
    });
  }

  function detailPanel(s){
    const inCart = cart.includes(s.id);
    const has360 = !!s.videoUrl;
    return `
      <tr class="detail-row"><td colspan="${columns.length}">
        <div class="detail-panel">
          <div class="detail-col rd-media">
            ${has360 ? `
            <div class="media-tabs" data-media-tabs="${s.id}">
              <button class="media-tab active" data-tab="photo">Photo</button>
              <button class="media-tab" data-tab="360">360&deg; View</button>
            </div>` : ``}
            <div class="media-stage" data-media-stage="${s.id}">
              ${stoneImg(s, "media-photo")}
              ${has360 ? `<div class="media-360" style="display:none;"></div>` : ``}
            </div>
            ${has360 ? `<div class="media-links"><a href="${s.videoUrl}" target="_blank" rel="noopener">Open full screen &#8599;</a></div>` : ``}
          </div>
          <div class="detail-col">
            <dl>
              <div class="dl"><dt>Stock #</dt><dd>${s.stock}</dd></div>
              <div class="dl"><dt>Stone Type</dt><dd>${s.type}</dd></div>
              <div class="dl"><dt>Shape</dt><dd>${s.shape}</dd></div>
              <div class="dl"><dt>Certificate #</dt><dd>${s.certNum || "\u2014"}</dd></div>
              <div class="dl"><dt>Lab</dt><dd>${s.lab}</dd></div>
              <div class="dl"><dt>Location</dt><dd>${s.location}</dd></div>
            </dl>
          </div>
          <div class="detail-col">
            <dl>
              <div class="dl"><dt>Quality</dt><dd>${s.quality || "\u2014"}</dd></div>
              <div class="dl"><dt>Color Tone</dt><dd>${s.colorTone || "\u2014"}</dd></div>
              <div class="dl"><dt>Color Hue</dt><dd>${s.colorHue || "\u2014"}</dd></div>
              <div class="dl"><dt>Heated</dt><dd>${s.heated || "No"}</dd></div>
              <div class="dl"><dt>Origin</dt><dd>${s.remarks || "Lab Grown"}</dd></div>
            </dl>
          </div>
          <div class="detail-col">
            <dl>
              <div class="dl"><dt>Measurements (mm)</dt><dd>${s.measurements || "\u2014"}</dd></div>
              <div class="dl"><dt>Carat</dt><dd>${s.weight.toFixed(2)}</dd></div>
              <div class="dl"><dt>Price / ct</dt><dd>${fmtMoney(s.unitPrice)}</dd></div>
              <div class="dl"><dt>Total Price</dt><dd>${fmtMoney(s.total)}</dd></div>
            </dl>
            <button class="add-btn ${inCart?"added":""}" style="margin-top:12px;width:100%;" data-id="${s.id}" data-cartbtn>
              ${inCart ? "Added \u2713" : "Add to Cart"}
            </button>
          </div>
        </div>
      </td></tr>
    `;
  }

  function renderTable(rows){
    const body = document.getElementById("tableBody");
    const empty = document.getElementById("emptyState");
    if(rows.length === 0){
      body.innerHTML = "";
      empty.style.display = "block";
      document.getElementById("tableCard").querySelector("table").style.display = "none";
      return;
    }
    empty.style.display = "none";
    document.getElementById("tableCard").querySelector("table").style.display = "table";

    let html = "";
    rows.forEach(s=>{
      const isExpanded = state.expanded === s.id;
      const inCart = cart.includes(s.id);
      html += `
        <tr class="row ${isExpanded?"expanded":""}" data-id="${s.id}">
          <td><div class="shape-cell"><span class="caret"></span>${stoneImg(s, "thumb")}<span>${s.shape}</span></div></td>
          <td>${s.type}</td>
          <td>${s.colorHue}${s.colorTone ? ` <span style="color:var(--text-faint);font-size:11.5px;">(${s.colorTone})</span>`:""}</td>
          <td>${s.quality || "\u2014"}</td>
          <td class="mono">${s.weight.toFixed(2)}</td>
          <td><span class="tag ${labClass(s.lab)}">${s.lab}</span></td>
          <td>${s.location}</td>
          <td class="mono col-price">${fmtMoney(s.unitPrice)}</td>
          <td class="mono price">${fmtMoney(s.total)}</td>
          <td><button class="add-btn ${inCart?"added":""}" data-id="${s.id}" data-cartbtn>${inCart?"Added \u2713":"Add to Cart"}</button></td>
        </tr>
      `;
      if(isExpanded){
        html += detailPanel(s);
      }
    });
    body.innerHTML = html;

    body.querySelectorAll("tr.row").forEach(tr=>{
      tr.addEventListener("click", (e)=>{
        if(e.target.closest("[data-cartbtn]")) return;
        const id = Number(tr.dataset.id);
        state.expanded = state.expanded === id ? null : id;
        render();
      });
    });
    body.querySelectorAll("[data-cartbtn]").forEach(btn=>{
      btn.addEventListener("click", (e)=>{
        e.stopPropagation();
        toggleCart(Number(btn.dataset.id));
      });
    });
    wireMediaTabs(body);
  }

  function wireMediaTabs(scope){
    scope.querySelectorAll("[data-media-tabs]").forEach(tabsEl=>{
      const id = tabsEl.dataset.mediaTabs;
      const stage = scope.querySelector(`[data-media-stage="${id}"]`);
      if(!stage) return;
      const photoEl = stage.querySelector(".media-photo");
      const viewEl = stage.querySelector(".media-360");
      const stone = STONES.find(x => String(x.id) === String(id));

      tabsEl.querySelectorAll(".media-tab").forEach(btn=>{
        btn.addEventListener("click", (e)=>{
          e.stopPropagation();
          tabsEl.querySelectorAll(".media-tab").forEach(b=>b.classList.remove("active"));
          btn.classList.add("active");
          if(btn.dataset.tab === "360"){
            photoEl.style.display = "none";
            viewEl.style.display = "block";
            if(!viewEl.dataset.loaded){
              viewEl.innerHTML = `<iframe src="${stone.videoUrl}" loading="lazy" allow="fullscreen" allowfullscreen referrerpolicy="no-referrer"></iframe>`;
              viewEl.dataset.loaded = "1";
            }
          } else {
            viewEl.style.display = "none";
            photoEl.style.display = "block";
          }
        });
      });
    });
  }

  // ---------- Rendering: grid ----------
  function renderGrid(rows){
    const grid = document.getElementById("gridView");
    if(rows.length === 0){ grid.innerHTML = ""; return; }
    grid.innerHTML = rows.map(s=>{
      const inCart = cart.includes(s.id);
      return `
        <div class="gcard" data-card-id="${s.id}">
          ${stoneImg(s, "gimg")}
          <div class="gbody">
            <div class="gtitle">${s.type} \u2014 ${s.shape}</div>
            <div class="gmeta">${s.weight.toFixed(2)} ct \u00b7 ${s.colorHue}</div>
            <div class="gmeta">${s.quality || "\u2014"} \u00b7 <span class="tag ${labClass(s.lab)}">${s.lab}</span></div>
            <div class="gfooter">
              <span class="gprice">${fmtMoney(s.total)}</span>
              <button class="add-btn ${inCart?"added":""}" data-id="${s.id}" data-cartbtn>${inCart?"Added \u2713":"Add"}</button>
            </div>
          </div>
        </div>
      `;
    }).join("");
    grid.querySelectorAll("[data-cartbtn]").forEach(btn=>{
      btn.addEventListener("click", (e)=>{
        e.stopPropagation();
        toggleCart(Number(btn.dataset.id));
      });
    });
    grid.querySelectorAll("[data-card-id]").forEach(card=>{
      card.addEventListener("click", (e)=>{
        if(e.target.closest("[data-cartbtn]")) return;
        openDetailModal(Number(card.dataset.cardId));
      });
    });
  }

  // ---------- Detail modal (used by grid / photo view) ----------
  function openDetailModal(id){
    const s = STONES.find(x=>x.id === id);
    if(!s) return;
    const inCart = cart.includes(s.id);
    const has360 = !!s.videoUrl;
    const box = document.getElementById("modalContent");

    box.innerHTML = `
      <div class="modal-head">
        <div>
          <div class="modal-title">${s.type} \u2014 ${s.shape}</div>
          <div class="modal-sub">Stock #${s.stock} \u00b7 ${s.weight.toFixed(2)} ct</div>
        </div>
        <button class="modal-close" id="modalCloseBtn">&times;</button>
      </div>
      <div class="modal-body">
        <div class="modal-media">
          ${has360 ? `
          <div class="media-tabs" data-media-tabs="${s.id}">
            <button class="media-tab active" data-tab="photo">Photo</button>
            <button class="media-tab" data-tab="360">360&deg; View</button>
          </div>` : ``}
          <div class="media-stage modal-stage" data-media-stage="${s.id}">
            ${stoneImg(s, "media-photo")}
            ${has360 ? `<div class="media-360" style="display:none;"></div>` : ``}
          </div>
          ${has360 ? `<div class="media-links"><a href="${s.videoUrl}" target="_blank" rel="noopener">Open full screen &#8599;</a></div>` : ``}
        </div>
        <div class="modal-specs">
          <dl>
            <div class="dl"><dt>Stock #</dt><dd>${s.stock}</dd></div>
            <div class="dl"><dt>Stone Type</dt><dd>${s.type}</dd></div>
            <div class="dl"><dt>Shape</dt><dd>${s.shape}</dd></div>
            <div class="dl"><dt>Carat</dt><dd>${s.weight.toFixed(2)}</dd></div>
            <div class="dl"><dt>Quality</dt><dd>${s.quality || "\u2014"}</dd></div>
            <div class="dl"><dt>Color Tone</dt><dd>${s.colorTone || "\u2014"}</dd></div>
            <div class="dl"><dt>Color Hue</dt><dd>${s.colorHue || "\u2014"}</dd></div>
            <div class="dl"><dt>Measurements (mm)</dt><dd>${s.measurements || "\u2014"}</dd></div>
            <div class="dl"><dt>Lab</dt><dd>${s.lab}</dd></div>
            <div class="dl"><dt>Certificate #</dt><dd>${s.certNum || "\u2014"}</dd></div>
            <div class="dl"><dt>Heated</dt><dd>${s.heated || "No"}</dd></div>
            <div class="dl"><dt>Origin</dt><dd>${s.remarks || "Lab Grown"}</dd></div>
            <div class="dl"><dt>Location</dt><dd>${s.location}</dd></div>
            <div class="dl"><dt>Price / ct</dt><dd>${fmtMoney(s.unitPrice)}</dd></div>
            <div class="dl"><dt>Total Price</dt><dd>${fmtMoney(s.total)}</dd></div>
          </dl>
          <button class="add-btn ${inCart?"added":""}" style="margin-top:14px;width:100%;" data-id="${s.id}" data-cartbtn>
            ${inCart ? "Added \u2713" : "Add to Cart"}
          </button>
        </div>
      </div>
    `;

    document.getElementById("modalOverlay").classList.add("open");
    document.getElementById("detailModal").classList.add("open");

    document.getElementById("modalCloseBtn").addEventListener("click", closeDetailModal);
    box.querySelectorAll("[data-cartbtn]").forEach(btn=>{
      btn.addEventListener("click", (e)=>{
        e.stopPropagation();
        const sid = Number(btn.dataset.id);
        toggleCart(sid);
        const nowIn = cart.includes(sid);
        btn.classList.toggle("added", nowIn);
        btn.textContent = nowIn ? "Added \u2713" : "Add to Cart";
      });
    });
    wireMediaTabs(box);
  }

  function closeDetailModal(){
    document.getElementById("modalOverlay").classList.remove("open");
    document.getElementById("detailModal").classList.remove("open");
    document.getElementById("modalContent").innerHTML = "";
  }

  // ---------- Cart ----------
  function toggleCart(id){
    const idx = cart.indexOf(id);
    if(idx === -1) cart.push(id); else cart.splice(idx,1);
    renderCartUI();
    render();
  }
  function renderCartUI(){
    document.getElementById("cartCount").textContent = cart.length;
    const items = cart.map(id => STONES.find(s=>s.id===id)).filter(Boolean);
    const body = document.getElementById("drawerBody");
    if(items.length === 0){
      body.innerHTML = `<div style="text-align:center;padding:40px 10px;color:var(--text-dim);font-size:13px;">Your cart is empty.</div>`;
    } else {
      body.innerHTML = items.map(s=>`
        <div class="cart-item">
          ${stoneImg(s, "ci-img")}
          <div class="ci-info">
            <div class="ci-title">${s.type} \u2014 ${s.shape}</div>
            <div class="ci-meta">${s.weight.toFixed(2)} ct \u00b7 Stock #${s.stock}</div>
            <button class="ci-remove" data-id="${s.id}">Remove</button>
          </div>
          <div class="ci-price">${fmtMoney(s.total)}</div>
        </div>
      `).join("");
      body.querySelectorAll(".ci-remove").forEach(btn=>{
        btn.addEventListener("click", ()=> toggleCart(Number(btn.dataset.id)));
      });
    }
    const total = items.reduce((sum,s)=>sum+s.total, 0);
    document.getElementById("drawerTotal").textContent = fmtMoney(total);
  }

  // ---------- Active filter chips ----------
  function renderChips(){
    const chips = [];
    if(state.shape) chips.push(["shape","Shape: "+state.shape]);
    if(state.hue) chips.push(["hue","Color: "+state.hue]);
    if(state.lab) chips.push(["lab","Lab: "+state.lab]);
    if(state.quality) chips.push(["quality","Quality: "+state.quality]);
    if(state.caratMin != null) chips.push(["caratMin","Min ct: "+state.caratMin]);
    if(state.caratMax != null) chips.push(["caratMax","Max ct: "+state.caratMax]);
    if(state.priceMin != null) chips.push(["priceMin","Min $: "+state.priceMin]);
    if(state.priceMax != null) chips.push(["priceMax","Max $: "+state.priceMax]);

    document.getElementById("activeChips").innerHTML = chips.map(([key,label])=>
      `<span class="chip">${label}<button data-clear="${key}">&times;</button></span>`
    ).join("");
    document.getElementById("activeChips").querySelectorAll("[data-clear]").forEach(btn=>{
      btn.addEventListener("click", ()=>{
        const key = btn.dataset.clear;
        if(key === "caratMin" || key === "caratMax" || key === "priceMin" || key === "priceMax"){
          state[key] = null;
          document.getElementById("f-"+key).value = "";
        } else {
          state[key] = "";
          document.getElementById("f-"+key).value = "";
        }
        render();
      });
    });
    document.getElementById("filterCountBadge").textContent = chips.length ? `(${chips.length})` : "";
  }

  // ---------- Main render ----------
  const CATEGORY_BLURBS = {
    "All": "Certified lab grown alexandrite, emerald, ruby, sapphire &amp; paraiba \u2014 live stock, updated pricing.",
    "Alexandrite": "Colour-change lab grown alexandrite \u2014 violetish blue to purple, AGL certified.",
    "Emerald": "Lab grown emerald in classic green \u2014 transparent, eye-clean material.",
    "Colombian Emerald": "Colombian-hue lab grown emerald, including vivid green selections.",
    "Paraiba": "Lab grown paraiba \u2014 the electric blue-green neon hue, in limited stock.",
    "Ruby": "Lab grown ruby in pigeon blood and classic red, AGL certified.",
    "Blue Sapphire": "Lab grown blue &amp; royal blue sapphire \u2014 the deepest saturation in our stock.",
    "Padparadscha": "The rare pink-orange sunset hue. Lab grown padparadscha sapphire, AGL certified.",
    "Pink Sapphire": "Soft to vivid pink lab grown sapphire \u2014 a limited selection.",
  };

  function renderHeading(){
    const cat = state.type;
    const titleEl = document.querySelector("#rd-gems .hero h1");
    const blurbEl = document.querySelector("#rd-gems .hero p");
    if(titleEl){
      titleEl.innerHTML = cat === "All"
        ? "Lab Grown Gemstone Inventory"
        : cat + " Inventory";
    }
    if(blurbEl){
      blurbEl.innerHTML = CATEGORY_BLURBS[cat] || CATEGORY_BLURBS["All"];
    }
    // Stats reflect the active category
    const scope = cat === "All" ? STONES : STONES.filter(s => category(s) === cat);
    const carats = scope.reduce((sum,x)=>sum+x.weight, 0);
    const shapes = uniqueSorted(scope.map(s=>s.shape)).length;
    const cEl = document.getElementById("statCount");
    const ctEl = document.getElementById("statCarat");
    const tEl = document.getElementById("statTypes");
    if(cEl) cEl.textContent = scope.length;
    if(ctEl) ctEl.textContent = carats.toFixed(0);
    if(tEl){
      tEl.textContent = shapes;
      const lbl = tEl.parentElement.querySelector(".lbl");
      if(lbl) lbl.textContent = "Shapes";
    }
  }

  function render(){
    const rows = getFiltered();
    document.getElementById("resultCount").textContent = rows.length;
    renderHeading();
    renderHead();
    renderChips();
    if(state.view === "list"){
      renderTable(rows);
    } else {
      renderGrid(rows);
    }
  }

  // ---------- Wire up static controls ----------
  function wireControls(){
    document.getElementById("filterToggleBtn").addEventListener("click", (e)=>{
      const panel = document.getElementById("filtersPanel");
      panel.classList.toggle("open");
      e.currentTarget.classList.toggle("active");
    });
    document.getElementById("clearFiltersBtn").addEventListener("click", ()=>{
      ["shape","hue","lab","quality"].forEach(k=>{ state[k]=""; document.getElementById("f-"+k).value=""; });
      ["caratMin","caratMax","priceMin","priceMax"].forEach(k=>{ state[k]=null; document.getElementById("f-"+k).value=""; });
      render();
    });

    document.getElementById("f-shape").addEventListener("change", e=>{ state.shape = e.target.value; render(); });
    document.getElementById("f-hue").addEventListener("change", e=>{ state.hue = e.target.value; render(); });
    document.getElementById("f-lab").addEventListener("change", e=>{ state.lab = e.target.value; render(); });
    document.getElementById("f-quality").addEventListener("change", e=>{ state.quality = e.target.value; render(); });
    document.getElementById("f-caratMin").addEventListener("input", e=>{ state.caratMin = e.target.value===""?null:Number(e.target.value); render(); });
    document.getElementById("f-caratMax").addEventListener("input", e=>{ state.caratMax = e.target.value===""?null:Number(e.target.value); render(); });
    document.getElementById("f-priceMin").addEventListener("input", e=>{ state.priceMin = e.target.value===""?null:Number(e.target.value); render(); });
    document.getElementById("f-priceMax").addEventListener("input", e=>{ state.priceMax = e.target.value===""?null:Number(e.target.value); render(); });

    document.getElementById("searchInput").addEventListener("input", e=>{ state.search = e.target.value; render(); });

    document.getElementById("listViewBtn").addEventListener("click", ()=>{
      state.view = "list";
      document.getElementById("listViewBtn").classList.add("active");
      document.getElementById("gridViewBtn").classList.remove("active");
      document.getElementById("tableCard").style.display = "block";
      document.getElementById("gridView").classList.remove("active");
      render();
    });
    document.getElementById("gridViewBtn").addEventListener("click", ()=>{
      state.view = "grid";
      document.getElementById("gridViewBtn").classList.add("active");
      document.getElementById("listViewBtn").classList.remove("active");
      document.getElementById("tableCard").style.display = "none";
      document.getElementById("gridView").classList.add("active");
      render();
    });

    document.getElementById("cartBtn").addEventListener("click", ()=>{
      document.getElementById("drawer").classList.add("open");
      document.getElementById("overlay").classList.add("open");
    });
    document.getElementById("drawerCloseBtn").addEventListener("click", closeDrawer);
    document.getElementById("overlay").addEventListener("click", closeDrawer);
    document.getElementById("modalOverlay").addEventListener("click", closeDetailModal);
    document.addEventListener("keydown", (e)=>{
      if(e.key === "Escape"){
        closeDetailModal();
        closeDrawer();
      }
    });
    function closeDrawer(){
      document.getElementById("drawer").classList.remove("open");
      document.getElementById("overlay").classList.remove("open");
    }
  }

  // ---------- Init ----------
  function init(){
    if(typeof RD_RAW === "undefined" || !document.getElementById("rd-gems")) return;
    populateFilters();
    populateTypeTabs();
    wireControls();
    renderCartUI();
    render();
  }
  // Scripts are deferred, so normally DOMContentLoaded has not fired yet.
  // But if the DOM is already parsed (e.g. re-injected by a section render),
  // the listener would never fire \u2014 so check readyState first.
  if(document.readyState === "loading"){
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
